import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { MouseEvent, ReactNode } from 'react'

import { isComposerTrigger, shouldHoldDialogOpen } from './notesDismiss'

type DismissEvent = { preventDefault: () => void }

type NotesDismissGuardValue = {
  warn: boolean
  /**
   * Reports whether `owner` holds an unsaved draft. The guard is dirty while
   * any owner is, so one owner clearing its draft leaves another's in place.
   * Callers that omit `owner` share one default owner.
   */
  setHasUnsavedDraft: (value: boolean, owner?: string) => void
  clearWarn: () => void
  interceptClose: () => boolean
  onPointerDownOutside: (event: DismissEvent) => void
  onEscapeKeyDown: (event: DismissEvent) => void
  onClickCapture: (event: MouseEvent) => void
  onComposerClick: (event: MouseEvent, close: () => void) => void
}

const DEFAULT_OWNER = 'default'

const NotesDismissGuardContext = createContext<NotesDismissGuardValue | null>(
  null,
)

export function NotesDismissGuardProvider({
  children,
}: {
  children: ReactNode
}): React.ReactElement {
  const [warn, setWarn] = useState(false)
  const alreadyWarned = useRef(false)
  const dirtyOwners = useRef(new Set<string>())

  const interceptClose = useCallback((): boolean => {
    const result = shouldHoldDialogOpen({
      nextOpen: false,
      isDirty: dirtyOwners.current.size > 0,
      alreadyWarned: alreadyWarned.current,
    })
    alreadyWarned.current = result.warned
    if (result.hold) {
      setWarn(true)
      return true
    }
    setWarn(false)
    return false
  }, [])

  const setHasUnsavedDraft = useCallback(
    (value: boolean, owner: string = DEFAULT_OWNER): void => {
      if (value) {
        dirtyOwners.current.add(owner)
        return
      }
      dirtyOwners.current.delete(owner)
      alreadyWarned.current = false
      setWarn(false)
    },
    [],
  )

  const clearWarn = useCallback((): void => {
    alreadyWarned.current = false
    setWarn(false)
  }, [])

  const onPointerDownOutside = useCallback(
    (event: DismissEvent): void => {
      if (interceptClose()) event.preventDefault()
    },
    [interceptClose],
  )

  const onEscapeKeyDown = useCallback(
    (event: DismissEvent): void => {
      if (interceptClose()) event.preventDefault()
    },
    [interceptClose],
  )

  const onClickCapture = useCallback(
    (event: MouseEvent): void => {
      if (!isComposerTrigger(event.target)) return
      if (interceptClose()) {
        event.preventDefault()
        event.stopPropagation()
      }
    },
    [interceptClose],
  )

  const onComposerClick = useCallback(
    (event: MouseEvent, close: () => void): void => {
      if (!isComposerTrigger(event.target)) return
      close()
    },
    [],
  )

  const value = useMemo(
    () => ({
      warn,
      setHasUnsavedDraft,
      clearWarn,
      interceptClose,
      onPointerDownOutside,
      onEscapeKeyDown,
      onClickCapture,
      onComposerClick,
    }),
    [
      clearWarn,
      interceptClose,
      onClickCapture,
      onComposerClick,
      onEscapeKeyDown,
      onPointerDownOutside,
      setHasUnsavedDraft,
      warn,
    ],
  )
  return (
    <NotesDismissGuardContext.Provider value={value}>
      {children}
    </NotesDismissGuardContext.Provider>
  )
}

export function useNotesDismissGuard(): NotesDismissGuardValue {
  const value = useContext(NotesDismissGuardContext)
  if (!value) {
    throw new Error('NotesDismissGuardProvider is required')
  }
  return value
}

export function useOptionalNotesDismissGuard(): NotesDismissGuardValue | null {
  return useContext(NotesDismissGuardContext)
}
